import {
  CopyOutlined,
  EditFilled,
  MinusOutlined,
  ScissorOutlined,
  SnippetsOutlined,
} from '@ant-design/icons';
import { Widget, WidgetRepository, isContainer } from '@framework/engine';
import { ButtonDeleteConfirm } from '@framework/ui';
import { Button, Space } from 'antd';
import React, { useCallback, useState } from 'react';
import { PreviewComponent } from '../../widgets/PreviewComponent';
import { SortableWidgetList } from '../SortableWidgetList';
import { useDndEditorContext } from '../hooks/useDndEditorContext';
import { useWidgetCloner } from '../hooks/useWidgetCloner';
import { dndLayoutSortingStrategy } from '../utils/dndLayoutSortingStrategy';
import { HoverActionsContainer } from './HoverActionsContainer';
import { SortableLeafWidgetRender } from './SortableLeafWidgetRender';
import { SortableParentWidgetRender } from './SortableParentWidgetRender';
import {
  ContentWrapper,
  WidgetContainerWrapper,
} from './SortableWidgetRender.styled';
import { SpaceForDragIndicator } from './SpaceForDragIndicator';

export interface ISortableWidgetRenderProps {
  widget: Widget;
  parent?: Widget;
  isSpaceForDrag?: boolean;

  // per il feedback visivo durante il drag and drop (all'hover)
  isDragOverlay?: boolean;
  isOverMe?: boolean;
  // l'utente sta spostano un widget (dragging)
  isMoving?: boolean;
}

/**
 * Elemento container per la visualizzazione di un Widget durante la configurazione.
 * Include un pulsante per l'apertura della modal di configurazione.
 * e le funzionalità per l'ordinamento tramite drag and drop.
 */
export function SortableWidgetRender(props: ISortableWidgetRenderProps) {
  const { isSpaceForDrag, parent, widget } = props;
  const widgetType = WidgetRepository.find(widget.type);
  const [isPopConfirmOpen, setIsPopConfirmOpen] = useState(false);

  const { configEditor, removeWidget } = useDndEditorContext();
  const clonerHandlers = useWidgetCloner();

  const handleCutWidget = useCallback(() => {
    clonerHandlers.cutWidget(widget);
  }, [clonerHandlers, widget]);

  const handleCopyWidget = useCallback(() => {
    clonerHandlers.copyWidget(widget);
  }, [clonerHandlers, widget]);

  const handlePasteWidget = useCallback(() => {
    clonerHandlers.pasteWidget(widget);
  }, [clonerHandlers, widget]);

  const handleRemoveWidget = useCallback(() => {
    removeWidget(widget);
  }, [removeWidget, widget]);

  const [compactWidgetHeight, setCompactWidgetHeight] = useState(false);

  const toggleCompactWidgetHeight = () => {
    setCompactWidgetHeight(!compactWidgetHeight);
  };

  return (
    <WidgetContainerWrapper>
      <ContentWrapper>
        {isSpaceForDrag ? (
          <SpaceForDragIndicator />
        ) : (
          <HoverActionsContainer
            isDragOverlay={props.isDragOverlay}
            isOverMe={props.isOverMe}
            isMoving={props.isMoving}
            isContainer={isContainer(widget)}
            isPopConfirmOpen={isPopConfirmOpen}
            isCompact={compactWidgetHeight}
            compactButtonProps={{
              onClick: toggleCompactWidgetHeight,
              children: compactWidgetHeight ? 'espandi' : 'contrai',
            }}
          >
            <Space>
              <Space.Compact>
                <Button
                  title="Taglia"
                  icon={<ScissorOutlined />}
                  loading={clonerHandlers.loadings.cut}
                  onClick={handleCutWidget}
                />
                <Button
                  title="Copia"
                  icon={<CopyOutlined />}
                  loading={clonerHandlers.loadings.copy}
                  onClick={handleCopyWidget}
                />
                <Button
                  title="Incolla"
                  icon={<SnippetsOutlined />}
                  loading={clonerHandlers.loadings.paste}
                  onClick={handlePasteWidget}
                />
              </Space.Compact>
              <ButtonDeleteConfirm
                title={`Eliminare l'elemento?`}
                iconOnlyButton
                // buttonDanger
                buttonType="default"
                buttonIcon={<MinusOutlined />}
                size="middle"
                onConfirm={handleRemoveWidget}
                onOpenChange={e => {
                  setIsPopConfirmOpen(e);
                }}
                // placement="leftBottom"
                align={{
                  offset: [4, -4],
                }}
              />
              <Button
                type="primary"
                icon={<EditFilled />}
                onClick={() => {
                  configEditor.openConfig(widget);
                }}
              />
            </Space>
          </HoverActionsContainer>
        )}

        {isContainer(widget) ? (
          <SortableParentWidgetRender
            widget={widget}
            parent={parent}
            isDragOverlay={props.isDragOverlay}
            isSpaceForDrag={isSpaceForDrag}
            isMoving={props.isMoving}
            compact={compactWidgetHeight}
          >
            <PreviewComponent widget={widget}>
              <SortableWidgetList
                widgets={widget.children ?? []}
                strategy={dndLayoutSortingStrategy(widgetType!)}
                parent={widget}
              />
            </PreviewComponent>
          </SortableParentWidgetRender>
        ) : (
          <SortableLeafWidgetRender
            compact={compactWidgetHeight}
            widget={widget}
            parent={parent}
          >
            <PreviewComponent widget={widget} />
          </SortableLeafWidgetRender>
        )}
      </ContentWrapper>
    </WidgetContainerWrapper>
  );
}
